/**
 * The clients whose GST portal session has died, as the signed-in person may see
 * them (noemailtick + gsturgent, 26 Sep 2026). ONE read, shared by the line under
 * the top bar (GstDisconnectTicker) and the fill rail's Urgent rows (FillRail), so
 * both always show the same clients and a save in one refreshes the other.
 *
 * `get_gst_disconnected_clients()` returns only the businesses the caller is on:
 * an office person sees every client they hold a seat on; a business's own people
 * see only their own business. Until migration 20260926045048 is applied the call
 * fails and the list is empty, said once in the console.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/shared/hooks/useAuth';
import { goToFix } from '@/shared/errors/goToFix';
import type { DisconnectedClient } from './gstDisconnected';

export const GST_DISCONNECTED_KEY = 'gst-disconnected-clients';

async function fetchDisconnected(): Promise<DisconnectedClient[]> {
    const { data, error } = await supabase.rpc('get_gst_disconnected_clients');
    if (error) {
        console.warn(`[gst-urgent] could not read disconnected GST clients (migration 20260926045048 applied?): ${error.message}`);
        return [];
    }
    return (data ?? []) as DisconnectedClient[];
}

export function useDisconnectedGst(): { clients: DisconnectedClient[]; refresh: () => void } {
    const userId = useAuth(s => s.user?.id ?? null);
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: [GST_DISCONNECTED_KEY, userId],
        queryFn: fetchDisconnected,
        enabled: !!userId,
        staleTime: 5 * 60_000,
        refetchInterval: 10 * 60_000,
        retry: false,
    });
    const refresh = useCallback(() => { void qc.invalidateQueries({ queryKey: [GST_DISCONNECTED_KEY] }); }, [qc]);
    return { clients: data ?? [], refresh };
}

/** The office line's "See all": the fill rail opens on its Urgent rows. */
export const GST_URGENT_EVENT = 'karr-gst-urgent:open';
export function openGstUrgent(): void {
    window.dispatchEvent(new Event(GST_URGENT_EVENT));
}

/**
 * Stand in the client's books, then land on `to` there. A seat on the client
 * (office people have one on every client) is a plain switch; otherwise the
 * office's Enter Client door.
 */
export function openClientAt(c: DisconnectedClient, to: string): void {
    const { orgs, currentOrg, switchOrg, enterClientOrg } = useAuth.getState();
    if (currentOrg?.id !== c.org_id) {
        if (orgs.some(o => o.id === c.org_id)) switchOrg(c.org_id);
        else enterClientOrg(c.org_id, (c.org_name ?? '').trim() || c.gstin);
    }
    void goToFix(to);
}

/**
 * Send the client the reconnect link now (sandbox-gst `gst_send_connect_link`,
 * which reads the portal username the client last connected with). A press is a
 * person's decision, so the 08:00-22:00 window of the automatic run does not apply.
 */
export async function sendReconnectLink(c: DisconnectedClient, username?: string): Promise<{ ok: boolean; error?: string; sentVia?: string }> {
    const { data: { session } } = await supabase.auth.getSession();
    try {
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sandbox-gst`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
            // boardfix: a never-connected GSTIN goes with the username a person typed.
            body: JSON.stringify({ org_id: c.org_id, action: 'gst_send_connect_link', gstin: c.gstin, ...(username ? { username } : {}) }),
        });
        const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string; sent_via?: string };
        if (!body.ok) return { ok: false, error: body.error || `The link could not be sent (the server answered ${res.status}).` };
        return { ok: true, sentVia: body.sent_via };
    } catch (e) {
        return { ok: false, error: `The link could not be sent: ${e instanceof Error ? e.message : String(e)}` };
    }
}
